import React, { useState } from 'react';
import { Body, Button, Checkbox, Modal, Title } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { term } from 'utils/project-ruleset';
import {
  GQLOperations,
  Milestone,
  MilestoneType,
  Rule,
  Ruleset,
} from 'types/dt-graphql-apollo-v2';
import { gql, useApolloClient } from '@apollo/client';
import { notification } from 'antd';
import { deleteMilestoneMutation } from 'utils/data-validation-graphql';

type MilestoneDeleteModalProps = {
  milestoneRules: Rule[];
  milestoneRulesets: Ruleset[];
  milestones: Milestone[];
  onCancel: VoidFunction;
  onDeleteMilestoneComplete: VoidFunction;
  selectedMilestone?: Milestone;
  visible: boolean;
};

export const MilestoneDeleteModal = ({
  milestoneRules,
  milestoneRulesets,
  milestones,
  onCancel,
  onDeleteMilestoneComplete,
  selectedMilestone,
  visible,
}: MilestoneDeleteModalProps) => {
  const apolloClient = useApolloClient();
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const handleMilestoneDeletion = async () => {
    if (!confirmDelete || !selectedMilestone) return;
    try {
      setIsLoadingDelete(true);

      const milestoneNameToBeDeleted = selectedMilestone.name;
      const milestoneTypeToBeDeleted = selectedMilestone.milestoneType;
      const query = deleteMilestoneMutation(
        selectedMilestone,
        milestones,
        milestoneRulesets,
        milestoneRules
      );
      await apolloClient.mutate({
        mutation: gql(query),
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
      notification.success({
        message: `Successfully deleted the "${milestoneNameToBeDeleted}"-${
          milestoneTypeToBeDeleted === MilestoneType.Default
            ? term.milestone
            : term.collection
        }`,
      });
      setConfirmDelete(false);
      onDeleteMilestoneComplete();
    } catch (e) {
      notification.error({
        message: `Error while deleting the ${term.milestone}`,
        description: e?.toString(),
      });
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const handleCancel = () => {
    setConfirmDelete(false);
    onCancel();
  };

  const renderHeader = () => (
    <StyledHeader>
      <StyledTitle level={3}>Delete {term.collection}</StyledTitle>
      <StyledBody level={2}>
        This collection may include {term.rulesets} and modifications of{' '}
        {term.rules} and {term.rulesets} that will be discarded when deleting
        the {term.collection}. Are you sure you want to delete it?
      </StyledBody>
    </StyledHeader>
  );

  const renderOption = () => (
    <StyledBody level={2}>
      <Checkbox
        name="confirmDelete"
        checked={confirmDelete}
        onChange={(_e, next) => setConfirmDelete(!!next)}
      >
        <span>
          Yes, I am sure I want to delete the{' '}
          <StyledSpanBold>"{selectedMilestone?.name}"</StyledSpanBold>-
          {term.collection}
        </span>
      </Checkbox>
    </StyledBody>
  );

  const renderFooterButtons = () => (
    <StyledButtons>
      <Button
        type="ghost"
        onClick={handleCancel}
        style={{ marginRight: '1rem' }}
      >
        Cancel
      </Button>
      <Button
        type="destructive"
        disabled={!confirmDelete}
        loading={isLoadingDelete}
        onClick={handleMilestoneDeletion}
      >
        Delete
      </Button>
    </StyledButtons>
  );

  return (
    <StyledModal visible={visible} onCancel={handleCancel} hideFooter={true}>
      {renderHeader()}
      {renderOption()}
      {renderFooterButtons()}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  overflow: hidden;
  width: 650px !important;
  max-width: calc(100% - 2rem);
  .cogs-modal-content {
    min-height: 200px;
    position: relative;
    overflow-wrap: break-word;
    padding: 0;
    margin: 8px;
  }
  .cogs-modal-header {
    padding: 0 0 0.5rem 1rem;
    font-size: 1.2rem;
    font-weight: 600;
  }
  .cogs-icon-Close {
    cursor: pointer;
  }
`;

const StyledHeader = styled.div`
  margin-bottom: 2rem;
`;

const StyledTitle = styled(Title)`
  margin: 0 0 1rem 0;
`;

const StyledBody = styled(Body)`
  margin: 1rem 0;
`;

const StyledButtons = styled.div`
  padding: 2rem 0 0 0;
  text-align: right;
`;

const StyledSpanBold = styled.span`
  font-weight: bold;
`;
